/* Built by Alpas Made. */

/* v1.19.74: defense-in-depth URL normalization for any user-supplied
   link rendered into the modal (website, future socials, etc.).
   Storefront would otherwise emit `<a href="boldbannds.com">` for a
   maker who typed their website without a protocol; the browser then
   resolves that against the current page, sending the buyer to
   `https://www.kineticcarving.com/pages/boldbannds.com` instead of the
   maker's actual site. Prepend `https://` when missing, reject
   dangerous protocols, return empty string for blank input. */
function makerNormalizeExternalUrl(raw) {
  if (!raw) return '';
  var v = String(raw).trim();
  if (!v) return '';
  /* Already a real http(s) URL: trust it as-is. */
  if (/^https?:\/\//i.test(v)) return v;
  /* Strip accidental leading punctuation like "://" or "//" so a value
     of "//boldbannds.com" still ends up as "https://boldbannds.com". */
  v = v.replace(/^[\s/:]+/, '');
  /* Reject any other scheme up front (javascript:, data:, vbscript:,
     file:, mailto:, tel: do not belong in a website field). Return
     empty so the caller can hide the link rather than render junk. */
  if (/^[a-z][a-z0-9+.\-]*:/i.test(v)) return '';
  return 'https://' + v;
}

/* HTML-escape a string before interpolating it into an attribute or
   text node we are building via innerHTML. The Liquid `escape` filter
   has already encoded values when we read them from `card.dataset.*`,
   but when those strings are re-emitted via innerHTML the entities are
   decoded back to literal characters. A maker who typed
   `https://x"><script>alert(1)</script>` would otherwise break out of
   the href attribute. */
function makerEscapeAttr(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

if (!customElements.get('maker-directory')) {
  customElements.define(
    'maker-directory',
    class MakerDirectory extends HTMLElement {
      constructor() {
        super();
        this.favoritesKey = 'kinetic-carving:maker-directory-favorites';
        this.viewKey = 'kinetic-carving:maker-directory-view';
        this.currentView = 'grid';
        this.perPage = parseInt(this.dataset.perPage) || 12;
        this.currentPage = 1;
        this.searchQuery = '';
        this.activeQuickFilter = 'all';
        /* Capability and project type are independent axes in the taxonomy:
           a buyer filters by a trade, by what gets made, or by both. They had
           shared activeQuickFilter, so picking one silently cleared the other.
           activeQuickFilter now carries only 'all' and 'favorites'. */
        this.activeCapability = '';
        this.activeProjectType = '';
        this.businessBadgeFilter = '';
        this.geoState = '';
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this._searchTimer = null;
      }

      connectedCallback() {
        this.grid = this.querySelector('[data-grid]');
        this.emptyState = this.querySelector('[data-empty]');
        this.resultCount = this.querySelector('[data-result-count]');
        this.sortSelect = this.querySelector('[data-sort]');
        this.cards = this.querySelectorAll('.maker-card');
        this.favoriteCountEls = this.querySelectorAll('[data-favorite-count]');
        this.loadMoreWrap = this.querySelector('[data-load-more-wrap]');
        this.loadMoreBtn = this.querySelector('[data-load-more]');
        this.showingEl = this.querySelector('[data-showing]');

        this.profileOverlay = this.querySelector('[data-modal-overlay="profile"]');
        this.lightbox = this.querySelector('[data-lightbox]');

        this.lightboxImages = [];
        this.lightboxIndex = 0;

        this.bindSearch();
        this.bindQuickFilters();
        this.bindSidebarFilters();
        this.bindSort();
        this.bindFavorites();
        this.bindCardClicks();
        this.bindClampRemeasure();
        this.bindModals();
        this.bindLightbox();
        this.bindShareButtons();
        this.bindGeolocation();
        this.bindViewToggle();
        this.bindFilterToggle();
        this.bindCategoryFilters();
        this.bindStatePills();
        this.bindPopularTags();
        this.bindHeroSearch();
        this.initStats();
        this.initEntranceAnimation();

        this.restoreFavorites();
        this.updateFavoriteCount();
        this.applyFilters();
      }

      /* ---- Search ---- */
      bindSearch() {
        var self = this;
        var input = this.querySelector('[data-search]');
        if (!input) return;
        input.addEventListener('input', function() {
          clearTimeout(self._searchTimer);
          self._searchTimer = setTimeout(function() {
            self.searchQuery = input.value.trim().toLowerCase();
            self.currentPage = 1;
            self.applyFilters();
          }, 300);
        });
      }

      /* ---- Quick Filters (mobile pills) ---- */
      bindQuickFilters() {
        var self = this;
        this.querySelectorAll('[data-quick-filter]').forEach(function(btn) {
          btn.addEventListener('click', function() {
            self.activeQuickFilter = btn.dataset.quickFilter;
            self.querySelectorAll('[data-quick-filter]').forEach(function(b) {
              b.setAttribute('aria-pressed', b.dataset.quickFilter === self.activeQuickFilter ? 'true' : 'false');
            });
            self.currentPage = 1;
            self.applyFilters();
          });
        });
      }

      /* ---- Sidebar Filters ---- */
      bindSidebarFilters() {
        var self = this;

        this.querySelectorAll('[data-filter-machine], [data-filter-material], [data-filter-service], [data-filter-favorites]').forEach(function(input) {
          input.addEventListener('change', function() {
            self.currentPage = 1;
            self.applyFilters();
          });
        });

        this.querySelectorAll('[data-filter-state], [data-filter-turnaround], [data-filter-pricing], [data-filter-experience]').forEach(function(select) {
          select.addEventListener('change', function() {
            self.currentPage = 1;
            self.applyFilters();
          });
        });

        this.querySelectorAll('[data-clear-filters]').forEach(function(btn) {
          btn.addEventListener('click', function() {
            self.clearFilters();
          });
        });
      }

      clearFilters() {
        var searchInput = this.querySelector('[data-search]');
        if (searchInput) { searchInput.value = ''; this.searchQuery = ''; }

        this.querySelectorAll('[data-filter-machine], [data-filter-material], [data-filter-service], [data-filter-favorites]').forEach(function(cb) {
          cb.checked = false;
        });

        this.querySelectorAll('[data-filter-state], [data-filter-turnaround], [data-filter-pricing], [data-filter-experience]').forEach(function(sel) {
          sel.value = '';
        });

        this.activeQuickFilter = 'all';
        this.activeCapability = '';
        this.activeProjectType = '';
        this.querySelectorAll('[data-project-type]').forEach(function(b) {
          b.setAttribute('aria-pressed', 'false');
        });
        this.querySelectorAll('[data-quick-filter]').forEach(function(b) {
          b.setAttribute('aria-pressed', b.dataset.quickFilter === 'all' ? 'true' : 'false');
        });

        /* v1.19.70: reset the Business badge pill group too. */
        this.businessBadgeFilter = '';
        this.querySelectorAll('[data-business-badge]').forEach(function(b) {
          b.setAttribute('aria-pressed', 'false');
        });

        if (this.geoState) {
          this.geoState = '';
          var geoStatus = this.querySelector('[data-geo-status]');
          if (geoStatus) geoStatus.style.display = 'none';
          var geoBtn = this.querySelector('[data-geo-btn]');
          if (geoBtn) geoBtn.classList.remove('is-active');
        }

        this.currentPage = 1;
        this.applyFilters();
      }

      getActiveFilters() {
        var filters = {
          machines: [],
          materials: [],
          services: [],
          state: '',
          turnaround: '',
          pricing: '',
          experience: '',
          favoritesOnly: false
        };

        this.querySelectorAll('[data-filter-machine]:checked').forEach(function(cb) {
          filters.machines.push(cb.dataset.filterMachine);
        });
        this.querySelectorAll('[data-filter-material]:checked').forEach(function(cb) {
          filters.materials.push(cb.dataset.filterMaterial);
        });
        this.querySelectorAll('[data-filter-service]:checked').forEach(function(cb) {
          filters.services.push(cb.dataset.filterService);
        });

        var stateSelect = this.querySelector('[data-filter-state]');
        if (stateSelect) filters.state = stateSelect.value;

        var turnaroundSelect = this.querySelector('[data-filter-turnaround]');
        if (turnaroundSelect) filters.turnaround = turnaroundSelect.value;

        var pricingSelect = this.querySelector('[data-filter-pricing]');
        if (pricingSelect) filters.pricing = pricingSelect.value;

        var experienceSelect = this.querySelector('[data-filter-experience]');
        if (experienceSelect) filters.experience = experienceSelect.value;

        var favCheckbox = this.querySelector('[data-filter-favorites]');
        if (favCheckbox) filters.favoritesOnly = favCheckbox.checked;

        return filters;
      }

      matchesFilters(card) {
        /* The promo card always matches every filter so the invitation
           stays visible regardless of state / capability / sort choices.
           It is anchored to grid position 0 by sortCards() below. */
        if (card.classList.contains('maker-card--promo')) return true;
        var filters = this.getActiveFilters();
        var favoriteIds = this.getFavoriteIds();

        /* Quick filter (sidebar pill or mobile chip). v1.3.0 lookups are
           against the new capabilities + project_types axes. A parent
           filter (e.g. "cap_laser") matches a maker tagged with a sub-type
           (e.g. "cap_laser_co2") because each card's data-capabilities
           string includes the parent handle as a substring of the
           sub-type. */
        if (this.activeQuickFilter === 'favorites') {
          if (favoriteIds.indexOf(card.dataset.makerId) === -1) return false;
        }
        if (this.activeCapability) {
          if ((card.dataset.capabilities || '').indexOf(this.activeCapability) === -1) return false;
        }
        if (this.activeProjectType) {
          if ((card.dataset.projectTypes || '').indexOf(this.activeProjectType) === -1) return false;
        }

        /* Search */
        if (this.searchQuery) {
          var haystack = (card.dataset.name + ' ' + card.dataset.city + ' ' + card.dataset.state + ' ' + card.dataset.description).toLowerCase();
          if (haystack.indexOf(this.searchQuery) === -1) return false;
        }

        /* Verified filter */
        if (this.verifiedFilter) {
          if (card.dataset.verified !== 'true') return false;
        }

        /* Business badge filter (v1.19.70). Single-select pill in the
           sidebar Business section. The card carries a comma-separated
           list of badge handles in data-business-badges; a substring
           match is enough because handles are unique tokens. */
        if (this.businessBadgeFilter) {
          var cardBadges = card.dataset.businessBadges || '';
          if (cardBadges.indexOf(this.businessBadgeFilter) === -1) return false;
        }

        /* Geo state filter. v1.19.85: match against the maker's FULL
           states list, not just the primary. Without this, a maker
           whose states are [Tennessee, Kentucky] would only appear
           under Tennessee even though they also serve Kentucky. The
           comma boundaries on both sides of the needle prevent
           substring collisions (e.g. "Virginia" matching inside
           "West Virginia"). Falls back to the legacy `data-state`
           singular for older cached HTML. */
        if (this.geoState) {
          var cardStatesGeo = card.dataset.states || ((card.dataset.state || '') + ',');
          if (cardStatesGeo.indexOf(this.geoState + ',') === -1) return false;
        }

        /* Sidebar: state. v1.19.85: same multi-state fix as the hero
           pill filter above. */
        if (filters.state) {
          var cardStatesSide = card.dataset.states || ((card.dataset.state || '') + ',');
          if (cardStatesSide.indexOf(filters.state + ',') === -1) return false;
        }

        /* Sidebar: machines (OR) */
        if (filters.machines.length > 0) {
          var cardMachines = card.dataset.machines || '';
          var hasAny = false;
          for (var i = 0; i < filters.machines.length; i++) {
            if (cardMachines.indexOf(filters.machines[i]) !== -1) { hasAny = true; break; }
          }
          if (!hasAny) return false;
        }

        /* Sidebar: materials (OR) */
        if (filters.materials.length > 0) {
          var cardMaterials = card.dataset.materials || '';
          var hasAnyMat = false;
          for (var j = 0; j < filters.materials.length; j++) {
            if (cardMaterials.indexOf(filters.materials[j]) !== -1) { hasAnyMat = true; break; }
          }
          if (!hasAnyMat) return false;
        }

        /* Sidebar: services (OR) */
        if (filters.services.length > 0) {
          var cardServices = card.dataset.services || '';
          var hasAnySvc = false;
          for (var k = 0; k < filters.services.length; k++) {
            if (cardServices.indexOf(filters.services[k]) !== -1) { hasAnySvc = true; break; }
          }
          if (!hasAnySvc) return false;
        }

        /* Sidebar: turnaround */
        if (filters.turnaround && card.dataset.turnaround !== filters.turnaround) return false;

        /* Sidebar: pricing */
        if (filters.pricing && card.dataset.pricing !== filters.pricing) return false;

        /* Experience */
        if (filters.experience) {
          var yrs = parseInt(card.dataset.years) || 0;
          if (filters.experience === '1_3' && (yrs < 1 || yrs > 3)) return false;
          if (filters.experience === '3_5' && (yrs < 3 || yrs > 5)) return false;
          if (filters.experience === '5_10' && (yrs < 5 || yrs > 10)) return false;
          if (filters.experience === '10_plus' && yrs < 10) return false;
        }

        /* Sidebar: favorites only */
        if (filters.favoritesOnly && favoriteIds.indexOf(card.dataset.makerId) === -1) return false;

        return true;
      }

      applyFilters() {
        var self = this;
        var matchingCards = [];

        this.cards.forEach(function(card) {
          if (self.matchesFilters(card)) {
            matchingCards.push(card);
          }
        });

        /* The promo card is in matchingCards so it stays visible, but it
           must not count toward the "X makers" total or the empty state.
           Real makers only for those tallies. */
        var realMatches = matchingCards.filter(function (c) {
          return !c.classList.contains('maker-card--promo');
        });
        var promoVisible = matchingCards.length > realMatches.length;
        var totalVisible = realMatches.length;

        /* v1.19.88: strict numbered pagination. Build a single
           "visual cards" array (promo first if visible, then real
           matches) and slice by page. This replaces the cumulative
           load-more behavior so clicking page 2 swaps to cards 13-24
           rather than appending them. */
        var visualCards = [];
        if (promoVisible) {
          var promo = null;
          for (var pi = 0; pi < matchingCards.length; pi++) {
            if (matchingCards[pi].classList.contains('maker-card--promo')) {
              promo = matchingCards[pi];
              break;
            }
          }
          if (promo) visualCards.push(promo);
        }
        for (var ri = 0; ri < realMatches.length; ri++) {
          visualCards.push(realMatches[ri]);
        }

        var totalPages = Math.max(1, Math.ceil(visualCards.length / this.perPage));
        if (this.currentPage > totalPages) this.currentPage = totalPages;
        if (this.currentPage < 1) this.currentPage = 1;

        var pageStart = (this.currentPage - 1) * this.perPage;
        var pageEnd = pageStart + this.perPage;

        this.cards.forEach(function(card) {
          card.setAttribute('hidden', '');
        });
        for (var vi = pageStart; vi < Math.min(pageEnd, visualCards.length); vi++) {
          visualCards[vi].removeAttribute('hidden');
        }

        /* Update result count to "Showing 13-24 of 58" when there are
           multiple pages, or just "58 makers" on a single page. */
        if (this.resultCount) {
          if (totalVisible === 0) {
            this.resultCount.textContent = '0 makers';
          } else if (totalPages > 1) {
            var firstShown = pageStart + 1;
            /* Subtract the promo from the visible count for the "of N"
               total so the maker counts the way buyers expect. */
            var pageEndShown = Math.min(pageEnd, visualCards.length);
            var realFirst, realLast;
            if (promoVisible && this.currentPage === 1) {
              realFirst = 1;
              realLast = Math.min(this.perPage - 1, realMatches.length);
            } else {
              realFirst = pageStart - (promoVisible ? 1 : 0) + 1;
              realLast = Math.min(pageEnd - (promoVisible ? 1 : 0), realMatches.length);
            }
            this.resultCount.textContent = 'Showing ' + realFirst + '-' + realLast + ' of ' + totalVisible + ' maker' + (totalVisible !== 1 ? 's' : '');
          } else {
            this.resultCount.textContent = totalVisible + ' maker' + (totalVisible !== 1 ? 's' : '');
          }
        }

        /* Toggle empty state */
        this.toggleEmpty(totalVisible === 0);

        /* Render pagination controls */
        this.renderPagination(totalPages);

        /* Legacy load-more wrap stays hidden (kept in DOM only for
           cached older JS that may still reference it). */
        if (this.loadMoreWrap) this.loadMoreWrap.setAttribute('hidden', '');

        /* Reveal the "Read more" cue only on cards whose bio is actually
           clipped. Runs here because a hidden card measures zero, so it has
           to be checked once the page's visible set is settled. */
        this.markClampedDescriptions();
      }

      /* Compare rendered height against full content height for each visible
         card's bio. `is-clamped` drives the "Read more" cue in CSS. The 1px
         tolerance absorbs sub-pixel rounding at fractional line heights,
         which otherwise flags a bio that fits exactly as clipped. */
      markClampedDescriptions() {
        if (!this.cards) return;
        this.cards.forEach(function(card) {
          var desc = card.querySelector('.maker-card__description');
          if (!desc) return;
          if (card.hidden || card.offsetParent === null) return;
          var clipped = desc.scrollHeight > desc.clientHeight + 1;
          card.classList.toggle('is-clamped', clipped);
        });
      }

      toggleEmpty(show) {
        if (this.emptyState) {
          this.emptyState.classList.toggle('is-visible', show);
        }
      }

      /* ---- Sorting ---- */
      bindSort() {
        if (!this.sortSelect) return;
        var self = this;
        this.sortSelect.addEventListener('change', function() {
          self.sortCards(self.sortSelect.value);
        });
        /* Apply the default sort on page load so verified makers rank above
           non-verified instead of relying on Shopify's alphabetical
           metaobject return order. */
        this.sortCards(this.sortSelect.value || 'featured');
      }

      sortCards(sortBy) {
        var self = this;
        var cardsArray = Array.from(this.cards);
        cardsArray.sort(function(a, b) {
          /* Promo card is always anchored to position 0 regardless of
             sort criteria. It is an invitation slot, not a maker, so it
             should never get reordered against real makers. */
          var aPromo = a.classList.contains('maker-card--promo');
          var bPromo = b.classList.contains('maker-card--promo');
          if (aPromo && !bPromo) return -1;
          if (bPromo && !aPromo) return 1;

          /* Featured + verified first; rest shuffled by daily seed.
             v1.19.89: replaces alphabetical tie-break with a stable
             per-day shuffle for non-featured / non-verified makers so
             alphabet-bottom shops (Zephyr, Yarra) rotate into top
             positions over time. The shuffle is seeded by today's
             date string so every visitor on the same day sees the
             same order (pagination + bookmarks stay consistent), but
             tomorrow's order is different. Featured and verified
             makers stay anchored to their earned positions. */
          if (sortBy === 'featured') {
            var aFeat = a.dataset.featured === 'true' ? 2 : 0;
            var bFeat = b.dataset.featured === 'true' ? 2 : 0;
            var aVerified = a.dataset.verified === 'true' ? 1 : 0;
            var bVerified = b.dataset.verified === 'true' ? 1 : 0;
            var diff = (bFeat + bVerified) - (aFeat + aVerified);
            if (diff !== 0) return diff;
            return self.dailyShuffleKey(a.dataset.makerId) - self.dailyShuffleKey(b.dataset.makerId);
          }
          if (sortBy === 'name-asc') return (a.dataset.name || '').localeCompare(b.dataset.name || '');
          if (sortBy === 'name-desc') return (b.dataset.name || '').localeCompare(a.dataset.name || '');
          if (sortBy === 'experience-desc') {
            return (parseInt(b.dataset.years) || 0) - (parseInt(a.dataset.years) || 0);
          }
          if (sortBy === 'turnaround-asc') {
            var order = { under_1_week: 1, '1_2_weeks': 2, '2_4_weeks': 3, over_4_weeks: 4 };
            return (order[a.dataset.turnaround] || 9) - (order[b.dataset.turnaround] || 9);
          }
          /* "Most Equipment" removed: the maker record has no machines or
             equipment field, only capabilities, so this sorted every card by
             an attribute that was never rendered and silently did nothing.
             Reinstating it needs a real equipment field, or a decision to
             relabel it as capability count. */

          return 0;
        });

        var grid = this.grid;
        cardsArray.forEach(function(card) { grid.appendChild(card); });
        this.cards = this.querySelectorAll('.maker-card');
        this.currentPage = 1;
        this.applyFilters();
      }

      /* ---- Pagination ---- */
      bindPagination() {
        /* Handled inline via load more button */
      }

      /* ---- Favorites ---- */
      bindFavorites() {
        var self = this;
        this.querySelectorAll('[data-favorite-btn]').forEach(function(btn) {
          if (btn._favBound) return;
          btn._favBound = true;
          btn.addEventListener('click', function(e) {
            e.stopPropagation();
            self.toggleFavorite(btn);
          });
        });

        if (this.loadMoreBtn) {
          this.loadMoreBtn.addEventListener('click', function() {
            self.currentPage++;
            self.applyFilters();
          });
        }

        /* v1.19.88: numbered pagination nav (replaces load-more). One
           delegated listener on the nav handles prev / next / numbered
           page clicks. Scrolls back to the grid top on every page
           change so the maker doesn't have to scroll up themselves. */
        var pag = this.querySelector('[data-pagination]');
        if (pag) {
          pag.addEventListener('click', function (e) {
            var btn = e.target.closest('[data-page-num], [data-page-prev], [data-page-next]');
            if (!btn || btn.disabled) return;
            var next;
            if (btn.hasAttribute('data-page-num')) {
              next = parseInt(btn.getAttribute('data-page-num'), 10);
            } else if (btn.hasAttribute('data-page-prev')) {
              next = self.currentPage - 1;
            } else if (btn.hasAttribute('data-page-next')) {
              next = self.currentPage + 1;
            }
            if (!Number.isFinite(next) || next < 1 || next === self.currentPage) return;
            self.currentPage = next;
            self.applyFilters();
            var grid = self.querySelector('[data-grid]');
            if (grid && typeof grid.scrollIntoView === 'function') {
              grid.scrollIntoView({
                behavior: self.prefersReducedMotion ? 'auto' : 'smooth',
                block: 'start',
              });
            }
          });
        }

        /* v1.20.2: re-render pagination on resize (debounced) so the
           number of page buttons adapts to the available width. Only
           re-renders when the slot budget actually crosses a
           breakpoint, so dragging the window edge does not thrash the
           DOM. No-op until the first applyFilters sets _lastTotalPages. */
        var resizeTimer = null;
        var lastSlots = self.paginationSlots();
        window.addEventListener('resize', function () {
          if (resizeTimer) clearTimeout(resizeTimer);
          resizeTimer = setTimeout(function () {
            var slots = self.paginationSlots();
            if (slots === lastSlots) return;
            lastSlots = slots;
            if (self._lastTotalPages) self.renderPagination(self._lastTotalPages);
          }, 150);
        });
      }

      /* v1.19.89: deterministic per-day shuffle key for a given maker id.
         Combines today's date (YYYY-MM-DD) with the maker id, hashes
         the result with a small 32-bit string hash (djb2-style), and
         returns the integer. Used as the tie-break in the "Featured"
         sort so non-featured / non-verified makers get a stable random
         position within their cohort that changes once per day. All
         visitors on the same day see the same hash for the same id, so
         pagination, bookmarks, and SEO stay consistent within the day. */
      dailyShuffleKey(makerId) {
        var d = new Date();
        /* Use local date components so all visitors in roughly the same
           hemisphere flip seeds around the same time. Storefront does
           not need UTC precision here. */
        var dateStr = d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
        var seed = dateStr + ':' + (makerId || '');
        /* djb2 hash. Fast, no deps, good enough distribution for
           shuffling a few hundred entries. */
        var hash = 5381;
        for (var i = 0; i < seed.length; i++) {
          hash = ((hash << 5) + hash) + seed.charCodeAt(i);
          hash = hash & hash; /* convert to 32-bit int */
        }
        return hash;
      }

      /* v1.20.2: how many page slots (numbers + ellipses) to render,
         chosen from the viewport width so a wide desktop fills the
         available space with real page numbers instead of collapsing
         to "1 2 ... 11". Mobile keeps a tight budget so the row never
         overflows at 375px. Re-evaluated on resize (debounced) by the
         listener in the events setup. */
      paginationSlots() {
        var w = window.innerWidth || document.documentElement.clientWidth || 1024;
        if (w >= 1200) return 12;
        if (w >= 992) return 11;
        if (w >= 750) return 9;
        if (w >= 560) return 7;
        return 5;
      }

      /* v1.20.2: compute which page numbers to render for a given
         current page and total, within a slot budget (maxButtons).
         If everything fits, every page shows. Otherwise a window is
         centered on the current page, shifted inward at the edges so
         it keeps its width near the start and end, with page 1 / last
         page always shown and ellipses filling the gaps. The window
         widens with the viewport (see paginationSlots).
            budget 12, 11 pages, any page:  1 2 3 4 5 6 7 8 9 10 11
            budget 9,  20 pages, page 1:    1 2 3 4 5 6 7 ... 20
            budget 9,  20 pages, page 10:   1 ... 8 9 10 11 12 ... 20
            budget 5,  20 pages, page 10:   1 ... 10 ... 20
      */
      computePageRange(total, current, maxButtons) {
        maxButtons = Math.max(maxButtons || 7, 5);
        if (total <= maxButtons) {
          var all = [];
          for (var i = 1; i <= total; i++) all.push(i);
          return all;
        }
        var windowSize = maxButtons - 2;
        var left = current - Math.floor((windowSize - 1) / 2);
        var right = current + Math.ceil((windowSize - 1) / 2);
        if (left < 1) { right += (1 - left); left = 1; }
        if (right > total) { left -= (right - total); right = total; }
        left = Math.max(left, 1);
        right = Math.min(right, total);

        /* Slot accounting: the window plus page 1, the last page, and
           up to two ellipses must all fit inside maxButtons. Trim from
           the side farther from the current page until it does, so the
           current page stays roughly centered. */
        function slotCount(l, r) {
          var c = (r - l + 1);
          if (l > 1) c += 1;          /* page 1 */
          if (l > 2) c += 1;          /* leading ellipsis */
          if (r < total) c += 1;      /* last page */
          if (r < total - 1) c += 1;  /* trailing ellipsis */
          return c;
        }
        while (slotCount(left, right) > maxButtons && right > left) {
          if ((current - left) >= (right - current)) left += 1;
          else right -= 1;
        }

        var pages = [];
        if (left > 1) {
          pages.push(1);
          if (left > 2) pages.push('ellipsis');
        }
        for (var p = left; p <= right; p++) pages.push(p);
        if (right < total) {
          if (right < total - 1) pages.push('ellipsis');
          pages.push(total);
        }
        return pages;
      }

      renderPagination(totalPages) {
        var nav = this.querySelector('[data-pagination]');
        if (!nav) return;
        var numbers = nav.querySelector('[data-page-numbers]');
        var prev = nav.querySelector('[data-page-prev]');
        var next = nav.querySelector('[data-page-next]');

        /* v1.20.2: remember the page count so the debounced resize
           listener can re-render with a new slot budget without a full
           applyFilters pass. */
        this._lastTotalPages = totalPages;

        if (totalPages <= 1) {
          nav.setAttribute('hidden', '');
          return;
        }

        nav.removeAttribute('hidden');

        if (prev) prev.disabled = this.currentPage <= 1;
        if (next) next.disabled = this.currentPage >= totalPages;

        if (numbers) {
          var html = '';
          var range = this.computePageRange(totalPages, this.currentPage, this.paginationSlots());
          for (var i = 0; i < range.length; i++) {
            var item = range[i];
            if (item === 'ellipsis') {
              html += '<span class="maker-directory__page-ellipsis" aria-hidden="true">...</span>';
            } else {
              var isCur = item === this.currentPage;
              html += '<button type="button" class="maker-directory__page-num' +
                (isCur ? ' is-current' : '') + '" data-page-num="' + item + '"' +
                (isCur ? ' aria-current="page"' : '') +
                ' aria-label="Page ' + item + '">' + item + '</button>';
            }
          }
          numbers.innerHTML = html;
        }
        /* v1.20.3: the "Page X of Y" status line was removed. The
           current page is already shown highlighted in the number row,
           and the result-count up top (aria-live) announces the new
           range on every page change for screen readers, so a separate
           status line was visually redundant and double-announced. */
      }

      toggleFavorite(btn) {
        var makerId = btn.dataset.makerId;
        var ids = this.getFavoriteIds();

        if (ids.indexOf(makerId) !== -1) {
          ids.splice(ids.indexOf(makerId), 1);
          btn.classList.remove('is-saved');
        } else {
          ids.push(makerId);
          btn.classList.add('is-saved');
        }

        this.saveFavoriteIds(ids);
        this.updateFavoriteCount();

        if (this.activeQuickFilter === 'favorites') {
          this.applyFilters();
        }
      }

      restoreFavorites() {
        var ids = this.getFavoriteIds();
        var self = this;
        ids.forEach(function(id) {
          var btn = self.querySelector('[data-favorite-btn][data-maker-id="' + id + '"]');
          if (btn) btn.classList.add('is-saved');
        });
      }

      updateFavoriteCount() {
        var count = this.getFavoriteIds().length;
        this.favoriteCountEls.forEach(function(el) {
          el.textContent = count;
        });
      }

      getFavoriteIds() {
        try { return JSON.parse(localStorage.getItem(this.favoritesKey) || '[]'); }
        catch (e) { return []; }
      }

      saveFavoriteIds(ids) {
        try { localStorage.setItem(this.favoritesKey, JSON.stringify(ids)); }
        catch (e) { /* Safari private mode */ }
      }

      /* ---- Geolocation ---- */
      bindGeolocation() {
        var self = this;
        var btn = this.querySelector('[data-geo-btn]');
        if (!btn) return;

        if (!navigator.geolocation) {
          btn.style.display = 'none';
          return;
        }

        btn.addEventListener('click', function() {
          if (self.geoState) {
            self.geoState = '';
            var status = self.querySelector('[data-geo-status]');
            if (status) status.style.display = 'none';
            btn.classList.remove('is-active');
            var textEl = btn.querySelector('[data-geo-text]');
            if (textEl) textEl.textContent = 'Find Near Me';
            self.currentPage = 1;
            self.applyFilters();
            return;
          }
          self.findNearMe(btn);
        });
      }

      findNearMe(btn) {
        var self = this;
        var textEl = btn.querySelector('[data-geo-text]');
        if (textEl) textEl.textContent = 'Locating...';
        btn.setAttribute('aria-busy', 'true');

        navigator.geolocation.getCurrentPosition(
          function(pos) {
            var lat = pos.coords.latitude;
            var lon = pos.coords.longitude;
            var url = 'https://nominatim.openstreetmap.org/reverse?format=json&lat=' + lat + '&lon=' + lon;

            fetch(url)
              .then(function(res) { return res.json(); })
              .then(function(data) {
                /* Cards carry FULL state names in data-states ("Tennessee,"),
                   so matching must use the name. The two-letter code is kept
                   only for the button label and status text, which is why the
                   name and the code are tracked separately. Matching on the
                   code was why Near Me never returned anyone.

                   Canada is stored as the state value on Canadian listings,
                   so a Canadian visitor matches on the country, not on the
                   province Nominatim returns. */
                var addr = (data && data.address) ? data.address : {};
                var isCanada = String(addr.country || '').toLowerCase() === 'canada';
                var stateName = isCanada ? 'Canada' : String(addr.state || '');
                var stateCode = isCanada ? 'Canada' : self.stateNameToCode(stateName);
                var geoLabel = stateCode || stateName;
                btn.setAttribute('aria-busy', 'false');
                if (stateName) {
                  self.geoState = stateName;
                  btn.classList.add('is-active');
                  if (textEl) textEl.textContent = geoLabel;
                  var status = self.querySelector('[data-geo-status]');
                  if (status) {
                    status.innerHTML = 'Showing makers in ' + geoLabel + ' <button type="button" data-geo-clear>Clear</button>';
                    status.style.display = 'flex';
                    var clearBtn = status.querySelector('[data-geo-clear]');
                    if (clearBtn) {
                      clearBtn.addEventListener('click', function() {
                        self.geoState = '';
                        status.style.display = 'none';
                        btn.classList.remove('is-active');
                        if (textEl) textEl.textContent = 'Find Near Me';
                        self.currentPage = 1;
                        self.applyFilters();
                      });
                    }
                  }
                  self.currentPage = 1;
                  self.applyFilters();
                } else {
                  if (textEl) textEl.textContent = 'Find Near Me';
                  var statusEl = self.querySelector('[data-geo-status]');
                  if (statusEl) {
                    statusEl.textContent = 'Could not determine your state.';
                    statusEl.style.display = 'flex';
                  }
                }
              })
              .catch(function() {
                btn.setAttribute('aria-busy', 'false');
                if (textEl) textEl.textContent = 'Find Near Me';
              });
          },
          function() {
            btn.setAttribute('aria-busy', 'false');
            if (textEl) textEl.textContent = 'Find Near Me';
            var statusEl = self.querySelector('[data-geo-status]');
            if (statusEl) {
              statusEl.textContent = 'Location access denied.';
              statusEl.style.display = 'flex';
            }
          },
          { timeout: 10000 }
        );
      }

      stateNameToCode(name) {
        var map = {
          'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
          'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
          'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
          'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
          'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
          'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
          'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
          'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
          'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
          'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
          'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
          'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
          'wisconsin': 'WI', 'wyoming': 'WY'
        };
        return map[(name || '').toLowerCase()] || '';
      }

      /* ---- Card Clicks -> Modal ---- */
      /* Card width changes the wrap point, so re-measure after a resize.
         Debounced because resize fires continuously while dragging. */
      bindClampRemeasure() {
        var self = this;
        if (this._clampResizeBound) return;
        this._clampResizeBound = true;
        var t = null;
        window.addEventListener('resize', function() {
          if (t) clearTimeout(t);
          t = setTimeout(function() { self.markClampedDescriptions(); }, 150);
        });
      }

      bindCardClicks() {
        var self = this;
        this.cards.forEach(function(card) {
          if (card._clickBound) return;
          card._clickBound = true;
          card.addEventListener('click', function() {
            self.openProfile(card);
          });
          card.addEventListener('keydown', function(e) {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              self.openProfile(card);
            }
          });
        });
      }

      openProfile(card) {
        if (!this.profileOverlay) return;

        var name = card.dataset.name || '';
        var city = card.dataset.city || '';
        var state = card.dataset.state || '';
        var description = card.dataset.description || '';
        var machines = card.dataset.machines || '';
        var materials = card.dataset.materials || '';
        var services = card.dataset.services || '';
        var turnaround = card.dataset.turnaround || '';
        var pricing = card.dataset.pricing || '';
        var verified = card.dataset.verified === 'true';
        /* v1.19.74: normalize the website value once here so every
           downstream renderer in this method (detail-grid link, quote
           CTA fallback, contact button) emits an absolute https URL
           even if the maker typed their domain without a protocol. */
        var website = makerNormalizeExternalUrl(card.dataset.website || '');
        var email = card.dataset.email || '';
        var phone = card.dataset.phone || '';
        var years = card.dataset.years || '';
        var portfolio = card.dataset.portfolio || '';

        /* Name */
        var nameEl = this.profileOverlay.querySelector('[data-modal-name]');
        if (nameEl) nameEl.textContent = name;

        /* Location */
        var locationEl = this.profileOverlay.querySelector('[data-modal-location]');
        if (locationEl) {
          var loc = '';
          if (city) loc += city;
          if (city && state) loc += ', ';
          if (state) loc += state;
          locationEl.textContent = loc;
        }

        /* Verified */
        var verifiedEl = this.profileOverlay.querySelector('[data-modal-verified]');
        if (verifiedEl) verifiedEl.style.display = verified ? 'inline-flex' : 'none';

        /* Avatar */
        var avatarWrap = this.profileOverlay.querySelector('[data-modal-avatar]');
        if (avatarWrap) {
          var cardImg = card.querySelector('.maker-card__avatar');
          if (cardImg) {
            avatarWrap.innerHTML = '<img src="' + cardImg.src + '" alt="' + name + '">';
          } else {
            avatarWrap.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="32" height="32" style="color:rgb(var(--color-foreground)/0.2)"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>';
          }
        }

        /* Populate dynamic modal body */
        var modalBody = this.profileOverlay.querySelector('[data-modal-dynamic]');
        if (modalBody) {
          var pricingLabels = { budget: 'Budget', mid: 'Mid-range', premium: 'Premium' };
          var turnaroundLabels = { under_1_week: 'Under 1 week', '1_2_weeks': '1-2 weeks', '2_4_weeks': '2-4 weeks', over_4_weeks: '4+ weeks' };
          var machineLabels = { cnc_router: 'CNC Router', laser_cutter: 'Laser Cutter', '3d_printer': '3D Printer', plasma_cutter: 'Plasma Cutter', cnc_mill: 'CNC Mill' };
          var matLabels = { wood: 'Wood', acrylic: 'Acrylic', metal: 'Metal', foam: 'Foam / HDU', mdf: 'MDF' };
          var svcLabels = { cutting: 'Cutting Service', finishing: 'Finishing', assembly: 'Assembly', custom: 'Custom Work', shipping: 'Ships Product' };

          var bodyHtml = '';

          /* About section */
          if (description) {
            bodyHtml += '<div class="maker-directory__modal-section">';
            bodyHtml += '<h4 class="maker-directory__modal-section-title">About</h4>';
            bodyHtml += '<p class="maker-directory__modal-description" style="white-space:pre-line">' + description + '</p>';
            bodyHtml += '</div>';
          }

          /* 2-column detail grid */
          bodyHtml += '<div class="maker-directory__modal-section"><div class="maker-directory__modal-details">';
          if (state) {
            bodyHtml += '<div class="maker-directory__modal-detail-item"><span class="maker-directory__modal-detail-label">State</span><span class="maker-directory__modal-detail-value">' + state + '</span></div>';
          }
          if (city) {
            bodyHtml += '<div class="maker-directory__modal-detail-item"><span class="maker-directory__modal-detail-label">City</span><span class="maker-directory__modal-detail-value">' + city + '</span></div>';
          }
          if (website) {
            var displayUrl = website.replace(/^https?:\/\//i, '').replace(/\/$/, '');
            bodyHtml += '<div class="maker-directory__modal-detail-item"><span class="maker-directory__modal-detail-label">Website</span><span class="maker-directory__modal-detail-value"><a href="' + makerEscapeAttr(website) + '" target="_blank" rel="noopener noreferrer">' + makerEscapeAttr(displayUrl) + '</a></span></div>';
          }
          if (phone) {
            bodyHtml += '<div class="maker-directory__modal-detail-item"><span class="maker-directory__modal-detail-label">Phone (call or text)</span><span class="maker-directory__modal-detail-value"><a href="tel:' + phone + '">' + phone + '</a></span></div>';
          }
          if (email) {
            bodyHtml += '<div class="maker-directory__modal-detail-item"><span class="maker-directory__modal-detail-label">Email</span><span class="maker-directory__modal-detail-value"><a href="mailto:' + email + '">' + email + '</a></span></div>';
          }
          if (turnaround) {
            bodyHtml += '<div class="maker-directory__modal-detail-item"><span class="maker-directory__modal-detail-label">Turnaround</span><span class="maker-directory__modal-detail-value">' + (turnaroundLabels[turnaround] || turnaround) + '</span></div>';
          }
          if (pricing) {
            bodyHtml += '<div class="maker-directory__modal-detail-item"><span class="maker-directory__modal-detail-label">Pricing</span><span class="maker-directory__modal-detail-value">' + (pricingLabels[pricing] || pricing) + '</span></div>';
          }
          if (years && parseInt(years) > 0) {
            bodyHtml += '<div class="maker-directory__modal-detail-item"><span class="maker-directory__modal-detail-label">Experience</span><span class="maker-directory__modal-detail-value">' + years + ' year' + (parseInt(years) !== 1 ? 's' : '') + '</span></div>';
          }
          bodyHtml += '</div></div>';

          /* Equipment tags with machine images */
          if (machines) {
            var machineImgData = card.dataset.machineImages || '';
            var machineImgMap = {};
            if (machineImgData) {
              machineImgData.split('||').forEach(function(entry) {
                if (entry.trim()) {
                  var parts = entry.split(':');
                  var key = parts[0];
                  var url = parts.slice(1).join(':');
                  if (key && url) machineImgMap[key.trim()] = url.trim();
                }
              });
            }
            var mTags = machines.split(',').filter(function(m) { return m.trim() && machineLabels[m.trim()]; }).map(function(m) {
              var mk = m.trim();
              var imgHtml = machineImgMap[mk] ? '<img src="' + machineImgMap[mk] + '" alt="' + machineLabels[mk] + '" class="maker-directory__modal-machine-img" loading="lazy">' : '';
              return '<div class="maker-directory__modal-machine-card">' + imgHtml + '<span>' + machineLabels[mk] + '</span></div>';
            }).join('');
            if (mTags) {
              bodyHtml += '<div class="maker-directory__modal-section"><h4 class="maker-directory__modal-section-title">Equipment</h4><div class="maker-directory__modal-machines">' + mTags + '</div></div>';
            }
          }

          /* Materials tags */
          if (materials) {
            var matTags = materials.split(',').filter(function(m) { return m.trim() && matLabels[m.trim()]; }).map(function(m) { return '<span class="maker-directory__modal-tag">' + matLabels[m.trim()] + '</span>'; }).join('');
            if (matTags) {
              bodyHtml += '<div class="maker-directory__modal-section"><h4 class="maker-directory__modal-section-title">Materials</h4><div class="maker-directory__modal-tags">' + matTags + '</div></div>';
            }
          }

          /* Services tags */
          if (services) {
            var svcTags = services.split(',').filter(function(s) { return s.trim() && svcLabels[s.trim()]; }).map(function(s) { return '<span class="maker-directory__modal-tag">' + svcLabels[s.trim()] + '</span>'; }).join('');
            if (svcTags) {
              bodyHtml += '<div class="maker-directory__modal-section"><h4 class="maker-directory__modal-section-title">Services</h4><div class="maker-directory__modal-tags">' + svcTags + '</div></div>';
            }
          }

          /* Portfolio gallery */
          this.lightboxImages = [];
          if (portfolio) {
            var imgs = portfolio.split('||').filter(function(u) { return u.trim(); });
            if (imgs.length > 0) {
              var gHtml = '';
              imgs.forEach(function(url, idx) {
                gHtml += '<img src="' + url.trim() + '" alt="Portfolio ' + (idx + 1) + '" data-gallery-img="' + idx + '" loading="lazy">';
              });
              bodyHtml += '<div class="maker-directory__modal-section"><h4 class="maker-directory__modal-section-title">Portfolio</h4><div class="maker-directory__modal-gallery">' + gHtml + '</div></div>';
              this.lightboxImages = imgs.map(function(u) { return u.trim(); });
            }
          }

          /* Customer Reviews Marquee */
          var cardRating = parseFloat(card.dataset.rating) || 0;
          var cardReviewCount = parseInt(card.dataset.reviewCount) || 0;
          if (cardRating > 0) {
            var starsHtml = '';
            for (var s = 1; s <= 5; s++) {
              if (s <= Math.floor(cardRating)) {
                starsHtml += '<svg class="maker-card__star maker-card__star--full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>';
              } else {
                starsHtml += '<svg class="maker-card__star maker-card__star--empty" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>';
              }
            }
            bodyHtml += '<div class="maker-directory__modal-section">';
            bodyHtml += '<h4 class="maker-directory__modal-section-title">Customer Reviews</h4>';
            bodyHtml += '<div class="maker-directory__modal-rating-summary">' + starsHtml + ' <span>' + cardRating + '/5' + (cardReviewCount > 0 ? ' (' + cardReviewCount + ' reviews)' : '') + '</span></div>';
            bodyHtml += '<div class="maker-directory__modal-reviews-marquee"><div class="maker-directory__modal-reviews-track">';
            var sampleReviews = [
              { text: 'Excellent quality and fast turnaround. Highly recommend!', author: 'Mike R.', stars: 5 },
              { text: 'Great communication throughout the project. Will use again.', author: 'Sarah L.', stars: 5 },
              { text: 'Beautiful work on my CNC project. Very professional.', author: 'James K.', stars: 4 },
              { text: 'Delivered exactly what I needed, on time and on budget.', author: 'Anna P.', stars: 5 },
              { text: 'Impressive attention to detail. Top-notch craftsmanship.', author: 'David W.', stars: 5 },
              { text: 'Quick response and fair pricing. Would definitely return.', author: 'Chris M.', stars: 4 }
            ];
            for (var r = 0; r < 2; r++) {
              sampleReviews.forEach(function(rev) {
                var revStars = '';
                for (var rs = 0; rs < rev.stars; rs++) revStars += '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="12" height="12" style="color:#F59E0B"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>';
                bodyHtml += '<div class="maker-directory__modal-review-card">';
                bodyHtml += '<div class="maker-directory__modal-review-stars">' + revStars + '</div>';
                bodyHtml += '<p class="maker-directory__modal-review-text">' + rev.text + '</p>';
                bodyHtml += '<span class="maker-directory__modal-review-author">' + rev.author + '</span>';
                bodyHtml += '</div>';
              });
            }
            bodyHtml += '</div></div>';
            bodyHtml += '</div>';
          }

          bodyHtml += '<hr class="maker-directory__modal-divider">';

          /* Request Quote CTA */
          if (email || website) {
            var quoteHref = email
              ? 'mailto:' + email + '?subject=' + encodeURIComponent('Quote Request from Kinetic Carving') + '&body=' + encodeURIComponent('Hi ' + name + ',\n\nI purchased a design from Kinetic Carving and would love to get a quote for having it made.\n\nDesign details:\n- Material: \n- Size: \n- Quantity: \n\nThank you!')
              : website;
            var quoteTarget = email ? '' : ' target="_blank" rel="noopener noreferrer"';
            bodyHtml += '<a href="' + makerEscapeAttr(quoteHref) + '"' + quoteTarget + ' class="maker-directory__modal-quote-cta">';
            bodyHtml += '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>';
            bodyHtml += 'Request a Quote</a>';
          }

          /* Contact buttons */
          var cHtml = '';
          if (website) {
            cHtml += '<a href="' + makerEscapeAttr(website) + '" target="_blank" rel="noopener noreferrer" class="maker-directory__modal-contact-btn maker-directory__modal-contact-btn--website">';
            cHtml += '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>';
            cHtml += 'Visit Website</a>';
          }
          if (phone) {
            cHtml += '<a href="tel:' + phone + '" class="maker-directory__modal-contact-btn maker-directory__modal-contact-btn--phone">';
            cHtml += '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>';
            cHtml += 'Call or text</a>';
          }
          if (email) {
            cHtml += '<a href="mailto:' + email + '" class="maker-directory__modal-contact-btn maker-directory__modal-contact-btn--email">';
            cHtml += '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 01-2.06 0L2 7"/></svg>';
            cHtml += 'Email</a>';
          }
          if (cHtml) {
            bodyHtml += '<div class="maker-directory__modal-contact">' + cHtml + '</div>';
          }

          modalBody.innerHTML = bodyHtml;

          /* Re-bind gallery clicks for lightbox */
          var galleryEl = modalBody.querySelector('.maker-directory__modal-gallery');
          if (galleryEl) {
            var self = this;
            galleryEl.addEventListener('click', function(e) {
              var img = e.target.closest('[data-gallery-img]');
              if (img) self.openLightbox(parseInt(img.dataset.galleryImg) || 0);
            });
          }
        }

        this.openModal(this.profileOverlay);
      }

      /* ---- Modals ---- */
      bindModals() {
        var self = this;

        this.querySelectorAll('[data-modal-close]').forEach(function(btn) {
          btn.addEventListener('click', function() {
            var overlay = btn.closest('[data-modal-overlay]');
            if (overlay) self.closeModal(overlay);
          });
        });

        this.querySelectorAll('[data-modal-overlay]').forEach(function(overlay) {
          overlay.addEventListener('click', function(e) {
            if (e.target === overlay) self.closeModal(overlay);
          });
        });

        document.addEventListener('keydown', function(e) {
          if (e.key === 'Escape') {
            /* Close lightbox first if open */
            if (self.lightbox && self.lightbox.classList.contains('is-open')) {
              self.closeLightbox();
              return;
            }
            var open = self.querySelector('[data-modal-overlay].is-open');
            if (open) self.closeModal(open);
          }
        });
      }

      openModal(overlay) {
        overlay.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        this.lastFocused = document.activeElement;

        var firstFocusable = overlay.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) firstFocusable.focus();

        this.trapFocus(overlay);
      }

      closeModal(overlay) {
        overlay.classList.remove('is-open');
        document.body.style.overflow = '';
        if (this.lastFocused) this.lastFocused.focus();
      }

      trapFocus(overlay) {
        var focusableEls = overlay.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusableEls.length === 0) return;
        var first = focusableEls[0];
        var last = focusableEls[focusableEls.length - 1];

        var handler = function(e) {
          if (e.key !== 'Tab') return;
          if (!overlay.classList.contains('is-open')) {
            overlay.removeEventListener('keydown', handler);
            return;
          }
          if (e.shiftKey) {
            if (document.activeElement === first) { e.preventDefault(); last.focus(); }
          } else {
            if (document.activeElement === last) { e.preventDefault(); first.focus(); }
          }
        };
        overlay.addEventListener('keydown', handler);
      }

      /* ---- Lightbox ---- */
      bindLightbox() {
        var self = this;
        if (!this.lightbox) return;

        /* Gallery clicks are bound dynamically in openProfile() */

        var closeBtn = this.lightbox.querySelector('[data-lightbox-close]');
        if (closeBtn) {
          closeBtn.addEventListener('click', function() { self.closeLightbox(); });
        }

        var prevBtn = this.lightbox.querySelector('[data-lightbox-prev]');
        if (prevBtn) {
          prevBtn.addEventListener('click', function() { self.navigateLightbox(-1); });
        }

        var nextBtn = this.lightbox.querySelector('[data-lightbox-next]');
        if (nextBtn) {
          nextBtn.addEventListener('click', function() { self.navigateLightbox(1); });
        }

        /* Click overlay to close */
        this.lightbox.addEventListener('click', function(e) {
          if (e.target === self.lightbox || e.target.classList.contains('maker-directory__lightbox-img-wrap')) {
            self.closeLightbox();
          }
        });

        /* Keyboard nav */
        document.addEventListener('keydown', function(e) {
          if (!self.lightbox.classList.contains('is-open')) return;
          if (e.key === 'ArrowLeft') self.navigateLightbox(-1);
          if (e.key === 'ArrowRight') self.navigateLightbox(1);
        });
      }

      openLightbox(index) {
        if (this.lightboxImages.length === 0) return;
        this.lightboxIndex = index;
        this.lightboxTrigger = document.activeElement;
        this.updateLightboxImage();
        this.lightbox.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }

      closeLightbox() {
        this.lightbox.classList.remove('is-open');
        /* Only restore scroll if modal is not also open */
        if (!this.profileOverlay || !this.profileOverlay.classList.contains('is-open')) {
          document.body.style.overflow = '';
        }
        if (this.lightboxTrigger) {
          this.lightboxTrigger.focus();
          this.lightboxTrigger = null;
        }
      }

      navigateLightbox(dir) {
        this.lightboxIndex += dir;
        if (this.lightboxIndex < 0) this.lightboxIndex = this.lightboxImages.length - 1;
        if (this.lightboxIndex >= this.lightboxImages.length) this.lightboxIndex = 0;
        this.updateLightboxImage();
      }

      updateLightboxImage() {
        var img = this.lightbox.querySelector('[data-lightbox-img]');
        var counter = this.lightbox.querySelector('[data-lightbox-counter]');
        var prevBtn = this.lightbox.querySelector('[data-lightbox-prev]');
        var nextBtn = this.lightbox.querySelector('[data-lightbox-next]');

        if (img) img.src = this.lightboxImages[this.lightboxIndex] || '';
        if (counter) counter.textContent = (this.lightboxIndex + 1) + ' / ' + this.lightboxImages.length;

        if (prevBtn) {
          if (this.lightboxImages.length <= 1) prevBtn.setAttribute('hidden', '');
          else prevBtn.removeAttribute('hidden');
        }
        if (nextBtn) {
          if (this.lightboxImages.length <= 1) nextBtn.setAttribute('hidden', '');
          else nextBtn.removeAttribute('hidden');
        }
      }

      /* ---- Share Buttons ---- */
      bindShareButtons() {
        var self = this;
        var copyBtn = this.querySelector('[data-share-copy]');
        var twitterBtn = this.querySelector('[data-share-twitter]');
        var facebookBtn = this.querySelector('[data-share-facebook]');

        if (copyBtn) {
          copyBtn.addEventListener('click', function() {
            var nameEl = self.profileOverlay ? self.profileOverlay.querySelector('[data-modal-name]') : null;
            var url = window.location.href.split('#')[0];
            if (nameEl && nameEl.textContent) {
              url += '#maker-' + encodeURIComponent(nameEl.textContent.toLowerCase().replace(/\s+/g, '-'));
            }
            if (navigator.clipboard) {
              navigator.clipboard.writeText(url).then(function() {
                copyBtn.classList.add('maker-directory__share-btn--copied');
                var textNode = copyBtn.lastChild;
                if (textNode && textNode.nodeType === 3) {
                  var old = textNode.textContent;
                  textNode.textContent = ' Copied!';
                  setTimeout(function() { textNode.textContent = old; copyBtn.classList.remove('maker-directory__share-btn--copied'); }, 2000);
                }
              });
            }
          });
        }

        if (twitterBtn) {
          twitterBtn.addEventListener('click', function() {
            var nameEl = self.profileOverlay ? self.profileOverlay.querySelector('[data-modal-name]') : null;
            var text = nameEl ? 'Check out ' + nameEl.textContent + ' on the Kinetic Carving Maker Directory!' : 'Check out the Kinetic Carving Maker Directory!';
            var url = 'https://twitter.com/intent/tweet?text=' + encodeURIComponent(text) + '&url=' + encodeURIComponent(window.location.href);
            window.open(url, '_blank', 'width=550,height=420');
          });
        }

        if (facebookBtn) {
          facebookBtn.addEventListener('click', function() {
            var url = 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(window.location.href);
            window.open(url, '_blank', 'width=550,height=420');
          });
        }
      }

      /* ---- View Toggle (Grid / List) ---- */
      bindViewToggle() {
        var self = this;
        try {
          var saved = localStorage.getItem(this.viewKey);
          if (saved === 'list') this.currentView = 'list';
        } catch (e) { /* Safari private */ }

        this.querySelectorAll('[data-view]').forEach(function(btn) {
          btn.addEventListener('click', function() {
            self.setView(btn.dataset.view);
          });
        });

        if (this.currentView === 'list') {
          this.setView('list');
        }
      }

      setView(view) {
        this.currentView = view;
        var grid = this.grid;
        if (!grid) return;

        if (view === 'list') {
          grid.classList.add('maker-directory__grid--list');
        } else {
          grid.classList.remove('maker-directory__grid--list');
        }

        this.querySelectorAll('[data-view]').forEach(function(btn) {
          btn.setAttribute('aria-pressed', btn.dataset.view === view ? 'true' : 'false');
        });

        try { localStorage.setItem(this.viewKey, view); }
        catch (e) { /* Safari private */ }
      }

      /* ---- Category Filters ---- */
      /* ---- Filter Sidebar Toggle ---- */
      bindFilterToggle() {
        var self = this;
        var toggleBtn = this.querySelector('[data-filter-toggle]');
        var sidebar = this.querySelector('[data-filter-sidebar]');
        var layout = this.querySelector('[data-layout]');
        var closeBtn = this.querySelector('[data-filter-sidebar-close]');
        if (!toggleBtn || !sidebar || !layout) return;

        toggleBtn.addEventListener('click', function() {
          var isOpen = sidebar.classList.contains('is-open');
          sidebar.classList.toggle('is-open', !isOpen);
          layout.classList.toggle('has-sidebar', !isOpen);
          toggleBtn.classList.toggle('is-active', !isOpen);
        });

        if (closeBtn) {
          closeBtn.addEventListener('click', function() {
            sidebar.classList.remove('is-open');
            layout.classList.remove('has-sidebar');
            toggleBtn.classList.remove('is-active');
          });
        }
      }

      bindCategoryFilters() {
        var self = this;
        this.querySelectorAll('[data-category]').forEach(function(btn) {
          btn.addEventListener('click', function() {
            var cat = btn.dataset.category;
            var wasOn = btn.getAttribute('aria-pressed') === 'true';
            var nextCat = (wasOn || cat === 'all') ? '' : cat;
            self.activeCapability = nextCat;
            self.querySelectorAll('[data-category]').forEach(function(b) {
              var on = nextCat ? b.dataset.category === nextCat : b.dataset.category === 'all';
              b.setAttribute('aria-pressed', on ? 'true' : 'false');
            });
            self.querySelectorAll('[data-quick-filter]').forEach(function(b) {
              b.setAttribute('aria-pressed', b.dataset.quickFilter === cat ? 'true' : 'false');
            });
            self.currentPage = 1;
            self.applyFilters();
          });
        });
        /* v1.19.70: Business badge pills (Veteran-owned / First responder
           / Woman-owned / Family-owned). Single-select within the badge
           group; click an active pill to deselect. This filter is
           independent from the capability + project-type quick filter,
           so a maker who is both "Woman-owned" AND tagged with a
           specific capability is included only when both filters match. */
        this.querySelectorAll('[data-business-badge]').forEach(function(btn) {
          btn.addEventListener('click', function() {
            var bg = btn.dataset.businessBadge;
            var isActive = btn.getAttribute('aria-pressed') === 'true';
            self.querySelectorAll('[data-business-badge]').forEach(function(b) {
              b.setAttribute('aria-pressed', 'false');
            });
            if (isActive) {
              self.businessBadgeFilter = '';
            } else {
              btn.setAttribute('aria-pressed', 'true');
              self.businessBadgeFilter = bg;
            }
            self.currentPage = 1;
            self.applyFilters();
          });
        });

        /* v1.3.0: Project Type pills. Single-select within the project-type
           group; clicking overrides any active capability filter on the
           same quick-filter slot. */
        this.querySelectorAll('[data-project-type]').forEach(function(btn) {
          btn.addEventListener('click', function() {
            var pt = btn.dataset.projectType;
            var isActive = btn.getAttribute('aria-pressed') === 'true';
            self.querySelectorAll('[data-project-type]').forEach(function(b) {
              b.setAttribute('aria-pressed', 'false');
            });
            if (isActive) {
              self.activeProjectType = '';
            } else {
              btn.setAttribute('aria-pressed', 'true');
              self.activeProjectType = pt;
            }
            self.currentPage = 1;
            self.applyFilters();
          });
        });
      }

      /* ---- State Pills ---- */
      bindStatePills() {
        var self = this;
        this.verifiedFilter = false;
        this.querySelectorAll('[data-state-pill]').forEach(function(btn) {
          btn.addEventListener('click', function() {
            var val = btn.dataset.statePill;

            if (val === 'all') {
              self.geoState = '';
              self.verifiedFilter = false;
              self.querySelectorAll('[data-state-pill]').forEach(function(b) {
                b.setAttribute('aria-pressed', b.dataset.statePill === 'all' ? 'true' : 'false');
              });
            } else if (val === 'verified') {
              var wasActive = btn.getAttribute('aria-pressed') === 'true';
              self.verifiedFilter = !wasActive;
              btn.setAttribute('aria-pressed', wasActive ? 'false' : 'true');
              /* Deselect "all" when verified is active */
              var allPill = self.querySelector('[data-state-pill="all"]');
              if (allPill) allPill.setAttribute('aria-pressed', 'false');
            } else {
              var isActive = btn.getAttribute('aria-pressed') === 'true';
              /* Deselect all pills first */
              self.querySelectorAll('[data-state-pill]').forEach(function(b) {
                if (b.dataset.statePill !== 'verified') b.setAttribute('aria-pressed', 'false');
              });
              if (isActive) {
                self.geoState = '';
                var allBtn = self.querySelector('[data-state-pill="all"]');
                if (allBtn) allBtn.setAttribute('aria-pressed', 'true');
              } else {
                btn.setAttribute('aria-pressed', 'true');
                self.geoState = val;
              }
            }
            self.currentPage = 1;
            self.applyFilters();
          });
        });
      }

      /* ---- Popular Tags ---- */
      bindPopularTags() {
        var self = this;
        this.querySelectorAll('[data-popular-tag]').forEach(function(btn) {
          btn.addEventListener('click', function() {
            var term = btn.dataset.popularTag;
            var searchInput = self.querySelector('[data-search]');
            var heroInput = self.querySelector('[data-hero-search]');
            if (searchInput) { searchInput.value = term; }
            if (heroInput) { heroInput.value = term; }
            self.searchQuery = term.toLowerCase();
            self.currentPage = 1;
            self.applyFilters();
          });
        });
      }

      /* ---- Hero Search (syncs with main search) ---- */
      bindHeroSearch() {
        var self = this;
        var heroInput = this.querySelector('[data-hero-search]');
        var heroBtn = this.querySelector('[data-hero-search-btn]');
        if (!heroInput) return;

        var timer = null;
        heroInput.addEventListener('input', function() {
          clearTimeout(timer);
          timer = setTimeout(function() {
            self.searchQuery = heroInput.value.trim().toLowerCase();
            /* Sync with sidebar search */
            var sidebarInput = self.querySelector('[data-search]');
            if (sidebarInput) sidebarInput.value = heroInput.value;
            self.currentPage = 1;
            self.applyFilters();
          }, 300);
        });

        if (heroBtn) {
          heroBtn.addEventListener('click', function() {
            self.searchQuery = heroInput.value.trim().toLowerCase();
            var sidebarInput = self.querySelector('[data-search]');
            if (sidebarInput) sidebarInput.value = heroInput.value;
            self.currentPage = 1;
            self.applyFilters();
          });
        }
      }

      /* ---- Stats Counter Animation ---- */
      initStats() {
        var counters = this.querySelectorAll('[data-stat-counter]');
        if (counters.length === 0) return;
        if (this.prefersReducedMotion) {
          counters.forEach(function(el) {
            el.textContent = el.dataset.statTarget || '0';
          });
          return;
        }

        var animated = false;
        var observer = new IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            if (entry.isIntersecting && !animated) {
              animated = true;
              counters.forEach(function(el) {
                var target = parseInt(el.dataset.statTarget) || 0;
                var duration = 800;
                var start = performance.now();
                function tick(now) {
                  var elapsed = now - start;
                  var progress = Math.min(elapsed / duration, 1);
                  var eased = 1 - Math.pow(1 - progress, 3);
                  el.textContent = Math.round(eased * target);
                  if (progress < 1) requestAnimationFrame(tick);
                }
                requestAnimationFrame(tick);
              });
              observer.disconnect();
            }
          });
        }, { threshold: 0.3 });

        var statsBar = this.querySelector('[data-stats-bar]');
        if (statsBar) observer.observe(statsBar);
      }

      /* ---- Staggered Entrance Animation ---- */
      initEntranceAnimation() {
        if (this.prefersReducedMotion) {
          this.querySelectorAll('[data-entrance]').forEach(function(card) {
            card.classList.add('is-entered');
          });
          return;
        }

        var observer = new IntersectionObserver(function(entries) {
          entries.forEach(function(entry) {
            if (entry.isIntersecting) {
              var card = entry.target;
              var index = Array.from(card.parentNode.children).indexOf(card);
              var delay = Math.min(index * 60, 400);
              setTimeout(function() {
                card.classList.add('is-entered');
              }, delay);
              observer.unobserve(card);
            }
          });
        }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

        this.querySelectorAll('[data-entrance]').forEach(function(card) {
          observer.observe(card);
        });
      }
    }
  );
}
